import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Plus, Truck } from "lucide-react";
import { QUALITY, TIKTOK_SHOP_URL } from "../data/product";
import { useAuth } from "../context/AuthContext";
import AuthModal from "./AuthModal";
import { Reveal, EASE } from "./Reveal";
import { useCart, PRODUCT } from "../context/CartContext";
import { useShopProduct } from "../lib/useShopProduct";

const LEFT_POINTS = [
  "Supports the Skin's Moisture Barrier",
  "Rich Yet Lightweight Texture",
  "Non-Greasy, Comfortable Finish",
];
const RIGHT_POINTS = [
  "Deep, Lasting Hydration",
  "Softens Dry, Rough Skin",
  "Powered by Skin-Loving Ingredients",
];

const Bullet = ({ children, delay }) => (
  <Reveal delay={delay} y={18}>
    <li className="flex items-start gap-2.5">
      <span className="mt-[8px] w-[4px] h-[4px] rounded-full bg-ink shrink-0" />
      <span className="font-sans text-[12px] md:text-[13px] text-ink/85 leading-relaxed">{children}</span>
    </li>
  </Reveal>
);

/* soft-edged white-bg photo that melts into the section */
const SoftShot = ({ src, alt, className = "" }) => (
  <img
    src={src}
    alt={alt}
    draggable={false}
    className={`w-full h-full object-cover [mask-image:radial-gradient(ellipse_at_center,black_55%,transparent_82%)] ${className}`}
  />
);

export default function ProductFeature() {
  const { addItem, openCart } = useCart();
  const live = useShopProduct();
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);

  const name = (live?.title || PRODUCT.name).split(/[–—|]/)[0].trim();
  const price = live?.variants?.[0]?.price ?? PRODUCT.price;

  const add = () => {
    addItem(
      {
        id: live?.handle || PRODUCT.id,
        name,
        size: "8 oz",
        price,
        image: "/images/jar-crop.png",
        amazonUrl: PRODUCT.amazonUrl,
        variantId:
          live?.variants?.find((v) => v.available !== false)?.id ||
          live?.variants?.[0]?.id ||
          null,
      },
      1
    );
    openCart();
  };

  return (
    <section
      id="shop"
      data-testid="product-section"
      className="relative bg-[#FDFCF9] pb-28 md:pb-36 overflow-hidden"
    >
      {/* giant wordmark — per the mockup */}
      <div className="relative z-20 mt-10 md:mt-14 px-6">
        <Reveal>
          <img
            src="/images/logo-vivore-black.png"
            alt="vivoré"
            className="mx-auto w-full max-w-[880px] h-auto select-none"
            draggable={false}
          />
        </Reveal>
      </div>

      {/* product stack — presentation above, the jar below — per the mockup */}
      <div className="relative mx-auto max-w-[1240px] px-6 md:px-12 mt-2 md:-mt-[4vw] grid grid-cols-1 md:grid-cols-[1fr_minmax(300px,460px)_1fr] gap-10 md:gap-8 items-start">
        <ul className="space-y-4 md:pt-36 max-w-[280px] mx-auto md:mx-0 pb-7 border-b border-ink/25 md:border-b-0">
          {LEFT_POINTS.map((p, i) => (
            <Bullet key={p} delay={0.15 + i * 0.1}>{p}</Bullet>
          ))}
        </ul>

        <div className="relative flex flex-col items-center">
          <motion.div
            className="relative w-[86vw] max-w-[540px] aspect-square"
            initial={{ opacity: 0, y: 60, scale: 0.96 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1.4, ease: EASE }}
          >
            <img
              data-testid="product-cream-image"
              src={live?.images?.[1] || "/images/cream-texture.jpg"}
              alt={`${name} — whipped cream texture with the jar`}
              draggable={false}
              className="w-full h-full object-cover [mask-image:radial-gradient(ellipse_at_center,black_55%,transparent_82%)]"
            />
          </motion.div>

          <Reveal delay={0.3} className="text-center">
            <img src="/images/logo-vi-black.png" alt="vi" className="h-9 md:h-10 w-auto mx-auto" />
            <h3 data-testid="product-name" className="mt-4 font-display text-2xl md:text-[1.7rem] text-ink leading-tight">
              {name}
            </h3>
            <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
              <p className="font-sans text-[11px] uppercase tracking-widest2 text-stone">
                8 oz — ${price.toFixed(2)}
              </p>
              <span
                data-testid="home-free-shipping-badge"
                className="flex items-center gap-1.5 bg-lavsoft text-lavdeep font-sans text-[9.5px] uppercase tracking-widest2 px-3 py-1.5 rounded-full"
              >
                <Truck size={11} strokeWidth={1.8} /> Free Shipping
              </span>
            </div>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-5">
              <button
                data-testid="add-to-cart-button"
                onClick={add}
                className="btn-lux group bg-ink text-ivory font-sans text-[11px] uppercase tracking-widest2 px-9 py-4 rounded-full flex items-center gap-2.5"
              >
                <Plus size={13} strokeWidth={2} className="transition-transform duration-500 group-hover:rotate-90" />
                Add to Cart
              </button>
              <a
                data-testid="shop-amazon-link"
                href={PRODUCT.amazonUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover-line inline-flex items-center gap-1.5 font-sans text-[11px] uppercase tracking-widest2 text-ink/60 hover:text-ink transition-colors duration-500"
              >
                Buy on Amazon <ArrowUpRight size={13} strokeWidth={1.5} />
              </a>
              <a
                data-testid="shop-tiktok-link"
                href={TIKTOK_SHOP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hover-line inline-flex items-center gap-1.5 font-sans text-[11px] uppercase tracking-widest2 text-ink/60 hover:text-ink transition-colors duration-500"
              >
                TikTok Shop <ArrowUpRight size={13} strokeWidth={1.5} />
              </a>
            </div>

            <div data-testid="home-quality-badges" className="mt-5 flex flex-wrap items-center justify-center gap-2.5">
              {QUALITY.map((q) => (
                <span
                  key={q.label}
                  className="flex items-center gap-1.5 bg-[#F8F5F2] border border-ink/10 rounded-full px-3.5 py-2"
                >
                  <q.icon size={11} strokeWidth={1.6} className="text-lavdeep" />
                  <span className="font-sans text-[9px] uppercase tracking-wide2 text-ink/75">{q.label}</span>
                </span>
              ))}
            </div>

            {user === false && (
              <button
                data-testid="home-member-offer"
                onClick={() => setAuthOpen(true)}
                className="mt-4 font-sans text-[11px] text-stone hover:text-ink transition-colors duration-500"
              >
                Join the list for <span className="text-lavdeep">10% off your first order</span>
              </button>
            )}
            <div className="mt-5">
              <Link
                to="/products/luxe-hydrating-cream"
                data-testid="view-full-details-link"
                className="hover-line inline-flex items-center gap-1.5 font-sans text-[11px] uppercase tracking-widest2 text-ink/60 hover:text-ink transition-colors duration-500"
              >
                View full details <ArrowUpRight size={12} strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
        </div>

        <ul className="space-y-4 md:pt-40 max-w-[280px] mx-auto md:mx-0 md:justify-self-end pb-7 border-b border-ink/25 md:border-b-0">
          {RIGHT_POINTS.map((p, i) => (
            <Bullet key={p} delay={0.3 + i * 0.1}>{p}</Bullet>
          ))}
        </ul>
      </div>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} initialTab="register" />
    </section>
  );
}
